/**
 * Signed URL support for project files / photos.
 *
 * Private project buckets must never fall back to getPublicUrl or raw
 * unsigned storage/REST URLs — that produces the browser JSON error:
 *   "No API key found in request"
 */
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  isSafeBrowserOpenUrl,
  isUnsafeSupabaseBrowserUrl,
  safeOpenInNewTab,
  UNSAFE_NAVIGATION_ERROR,
} from '@/lib/safeBrowserNavigation';

/** Minimal shape a record needs to resolve a display URL. */
export interface FileStorageRef {
  id?: string;
  storage_bucket?: string | null;
  storage_path?: string | null;
  file_url?: string | null;
}

interface StorageTarget {
  bucket: string;
  path: string;
}

/** Default signed-URL lifetime: 1 hour. */
export const DEFAULT_SIGNED_URL_EXPIRES_IN = 60 * 60;

export const FILE_OPEN_ERROR_MESSAGE = UNSAFE_NAVIGATION_ERROR;

/**
 * App buckets treated as private for open/display resolution.
 * Signing is required; getPublicUrl fallback is forbidden.
 */
const PRIVATE_STORAGE_BUCKETS = new Set([
  'project-files',
  'daily-log-photos',
  'project-photos',
  'instruction-attachments',
  'change-order-photos',
]);

export type OpenProjectFileResult =
  | { ok: true; url: string }
  | { ok: false; error: string };

function normalizeBucket(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') return null;
  const normalized = value.trim().replace(/^\/+|\/+$/g, '');
  return normalized || null;
}

function decodePath(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Parse public, authenticated, or signed Supabase Storage object URLs. */
function parseStorageObjectUrl(value: string | null | undefined): StorageTarget | null {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const parsed = new URL(trimmed, 'https://storage-path.invalid');
    const match = parsed.pathname.match(
      /\/storage\/v1\/object\/(?:public|authenticated|sign)\/([^/]+)\/(.+)$/i
    );
    if (!match) return null;

    const bucket = normalizeBucket(decodePath(match[1]));
    const path = decodePath(match[2]).replace(/^\/+/, '');
    if (!bucket || !path) return null;
    return { bucket, path };
  } catch {
    return null;
  }
}

function normalizeObjectPath(value: string | null | undefined): string | null {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  const withoutQuery = trimmed.split(/[?#]/, 1)[0];
  const normalized = decodePath(withoutQuery).replace(/^\/+/, '');
  return normalized || null;
}

/**
 * Build all credible bucket/path combinations for legacy and current rows.
 * Some historical rows store a full Storage URL, while others store an object
 * path with the bucket name repeated as its first directory. Try the exact
 * stored path first, then safe normalized alternatives.
 */
function buildStorageTargets(file: FileStorageRef): StorageTarget[] {
  const targets: StorageTarget[] = [];
  const seen = new Set<string>();

  const add = (bucketValue: string | null | undefined, pathValue: string | null | undefined) => {
    const bucket = normalizeBucket(bucketValue);
    const path = normalizeObjectPath(pathValue);
    if (!bucket || !path) return;
    const key = `${bucket}\n${path}`;
    if (seen.has(key)) return;
    seen.add(key);
    targets.push({ bucket, path });
  };

  const explicitBucket = normalizeBucket(file.storage_bucket);
  const parsedStoredPath = parseStorageObjectUrl(file.storage_path);

  if (parsedStoredPath) {
    add(parsedStoredPath.bucket, parsedStoredPath.path);
  } else if (explicitBucket) {
    const storedPath = normalizeObjectPath(file.storage_path);
    add(explicitBucket, storedPath);

    // Legacy rows may have saved "bucket/object/path" instead of "object/path".
    // Keep the exact candidate first because some uploads genuinely used that
    // prefixed object key, then try the de-prefixed form as a fallback.
    if (storedPath?.startsWith(`${explicitBucket}/`)) {
      add(explicitBucket, storedPath.slice(explicitBucket.length + 1));
    }
  }

  const parsedFileUrl = parseStorageObjectUrl(file.file_url);
  if (parsedFileUrl) {
    add(parsedFileUrl.bucket, parsedFileUrl.path);
  }

  return targets;
}

/** True when the record carries enough info to mint a signed URL. */
export function hasStorageRef(file: FileStorageRef): boolean {
  return buildStorageTargets(file).length > 0;
}

/** Reject REST API endpoints and other unusable URL strings. */
export function isInvalidSupabaseApiUrl(url: string): boolean {
  return isUnsafeSupabaseBrowserUrl(url);
}

/** True for a normal http(s) URL that is safe to open/display. */
export function isUsableHttpFileUrl(url: string): boolean {
  return isSafeBrowserOpenUrl(url);
}

function logUnresolvedFileUrl(file: FileStorageRef, reason: string): void {
  // eslint-disable-next-line no-console
  console.warn('[resolveProjectFileOpenUrl]', reason, {
    fileId: file.id ?? null,
    storageBucket: file.storage_bucket ?? null,
    storagePath: file.storage_path ?? null,
    hasFileUrl: Boolean(file.file_url),
  });
}

function isPrivateBucket(bucket: string | null | undefined): boolean {
  return Boolean(bucket && PRIVATE_STORAGE_BUCKETS.has(bucket));
}

/**
 * Resolve a browser-openable URL for a project file.
 * Never returns a Supabase REST API URL or unsigned private storage URL.
 */
export async function resolveProjectFileOpenUrl(
  file: FileStorageRef,
  expiresIn: number = DEFAULT_SIGNED_URL_EXPIRES_IN
): Promise<{ url: string | null; error?: string }> {
  const targets = buildStorageTargets(file);
  let privateTargetAttempted = false;
  let lastStorageError = '';

  for (const target of targets) {
    privateTargetAttempted = privateTargetAttempted || isPrivateBucket(target.bucket);

    try {
      const { data, error } = await supabase.storage
        .from(target.bucket)
        .createSignedUrl(target.path, expiresIn);

      if (!error && data?.signedUrl && isSafeBrowserOpenUrl(data.signedUrl)) {
        return { url: data.signedUrl };
      }

      if (error?.message) lastStorageError = error.message;
    } catch (err) {
      lastStorageError = err instanceof Error ? err.message : String(err);
    }
  }

  // Private buckets: never fall back to getPublicUrl or unsigned file_url.
  if (privateTargetAttempted) {
    logUnresolvedFileUrl(
      file,
      lastStorageError
        ? `Signed URL failed for private storage target: ${lastStorageError}`
        : 'Signed URL missing for private storage target'
    );
    return { url: null, error: FILE_OPEN_ERROR_MESSAGE };
  }

  const fallback = (file.file_url || '').trim();
  if (isSafeBrowserOpenUrl(fallback)) {
    return { url: fallback };
  }

  logUnresolvedFileUrl(
    file,
    fallback && isUnsafeSupabaseBrowserUrl(fallback)
      ? 'Rejected invalid Supabase API or unsigned storage file_url'
      : 'No valid storage reference or file_url'
  );

  return { url: null, error: FILE_OPEN_ERROR_MESSAGE };
}

/**
 * Synchronous best-effort display URL with no network call.
 * Returns a validated legacy `file_url` placeholder only (never REST/unsigned storage).
 */
export function resolveFileDisplayUrl(file: FileStorageRef): string {
  const fallback = (file.file_url || '').trim();
  return isSafeBrowserOpenUrl(fallback) ? fallback : '';
}

/**
 * Create a signed URL from `storage_bucket` + `storage_path` or a parsable
 * Supabase Storage URL. Never throws.
 */
export async function createFileSignedUrl(
  file: FileStorageRef,
  expiresIn: number = DEFAULT_SIGNED_URL_EXPIRES_IN
): Promise<string> {
  const resolved = await resolveProjectFileOpenUrl(file, expiresIn);
  return resolved.url || '';
}

/** Open a project file in a new browser tab using a resolved storage URL. */
export async function openProjectFileInNewTab(
  file: FileStorageRef & { file_name?: string | null }
): Promise<OpenProjectFileResult> {
  const resolved = await resolveProjectFileOpenUrl(file);
  if (!resolved.url) {
    return { ok: false, error: resolved.error || FILE_OPEN_ERROR_MESSAGE };
  }

  const opened = safeOpenInNewTab(resolved.url, {
    source: 'openProjectFileInNewTab',
    fileId: file.id ?? null,
    bucket: file.storage_bucket ?? null,
    path: file.storage_path ?? null,
  });

  if (!opened.ok) {
    return { ok: false, error: opened.error || FILE_OPEN_ERROR_MESSAGE };
  }
  return { ok: true, url: opened.url! };
}

/**
 * React hook: resolve display URLs for a list of file/photo records.
 *
 * Returns a map of `record.id -> display URL`. Seeded with safe file_url
 * placeholders, then patched with signed URLs once they resolve.
 */
export function useSignedFileUrls<T extends FileStorageRef & { id: string }>(
  files: T[],
  expiresIn: number = DEFAULT_SIGNED_URL_EXPIRES_IN
): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});

  const signature = useMemo(
    () =>
      files
        .map(
          (f) =>
            `${f.id}|${f.storage_bucket ?? ''}|${f.storage_path ?? ''}|${f.file_url ?? ''}`
        )
        .join('::'),
    [files]
  );

  useEffect(() => {
    let cancelled = false;

    const seed: Record<string, string> = {};
    for (const f of files) seed[f.id] = resolveFileDisplayUrl(f);
    setUrls(seed);

    if (files.length === 0) return;

    (async () => {
      const entries = await Promise.all(
        files.map(async (f) => [f.id, await createFileSignedUrl(f, expiresIn)] as const)
      );
      if (cancelled) return;
      setUrls((prev) => {
        const next = { ...prev };
        for (const [id, url] of entries) {
          if (url) next[id] = url;
        }
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, expiresIn]);

  return urls;
}
